import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { CheckCircle2 } from 'lucide-react';
import { getApprovalQueue } from '../../api/orders';
import { PageHeader } from '../../components/ui/PageHeader';
import { Card } from '../../components/ui/Card';
import { Table, Thead, Th, Tr, Td } from '../../components/ui/Table';
import { Spinner } from '../../components/ui/Spinner';
import { EmptyState } from '../../components/ui/EmptyState';
import { Button } from '../../components/ui/Button';
import { ApproveOrderModal } from './ApproveOrderModal';
import { RejectOrderModal } from './RejectOrderModal';
import type { Order } from '../../types/api';

function money(lines: { priceSnapshot: string; quantity: number }[]) {
  const total = lines.reduce((sum, l) => sum + Number(l.priceSnapshot) * l.quantity, 0);
  return `£${total.toFixed(2)}`;
}

export function ApprovalQueuePage() {
  const queueQuery = useQuery({ queryKey: ['orders-queue'], queryFn: getApprovalQueue });
  const [approveTarget, setApproveTarget] = useState<Order | null>(null);
  const [rejectTarget, setRejectTarget] = useState<Order | null>(null);

  return (
    <div>
      <PageHeader title="Approvals" description="Orders awaiting your decision at sites where you're a signing authority." />

      <Card>
        {queueQuery.isLoading ? (
          <div className="py-14 text-center">
            <Spinner />
          </div>
        ) : !queueQuery.data || queueQuery.data.length === 0 ? (
          <EmptyState icon={CheckCircle2} title="Nothing waiting" description="You're all caught up." />
        ) : (
          <Table>
            <Thead>
              <tr>
                <Th>Reference</Th>
                <Th>Requested by</Th>
                <Th>Site</Th>
                <Th>Total</Th>
                <Th>Submitted</Th>
                <Th />
              </tr>
            </Thead>
            <tbody>
              {queueQuery.data.map((order) => (
                <Tr key={order.id}>
                  <Td className="font-medium text-slate-900">
                    <Link to={`/orders/${order.id}`} className="hover:text-brand-700">
                      {order.reference}
                    </Link>
                  </Td>
                  <Td>
                    {order.requestingUser
                      ? [order.requestingUser.firstName, order.requestingUser.lastName].filter(Boolean).join(' ') ||
                        order.requestingUser.email
                      : '-'}
                  </Td>
                  <Td>{order.site?.name ?? '-'}</Td>
                  <Td>{money(order.lines)}</Td>
                  <Td>{new Date(order.createdAt).toLocaleDateString()}</Td>
                  <Td className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button size="sm" variant="danger" onClick={() => setRejectTarget(order)}>
                        Reject
                      </Button>
                      <Button size="sm" onClick={() => setApproveTarget(order)}>
                        Approve
                      </Button>
                    </div>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      {approveTarget && (
        <ApproveOrderModal order={approveTarget} open={!!approveTarget} onClose={() => setApproveTarget(null)} />
      )}
      {rejectTarget && (
        <RejectOrderModal order={rejectTarget} open={!!rejectTarget} onClose={() => setRejectTarget(null)} />
      )}
    </div>
  );
}
